/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isString from 'lodash.isstring';
import {entityId, setRel} from '@ion/core/src/services/ConnectUtils';
import {Asset, GenericObject, GroupMembers} from '@ion/core/src/types/entities';
import {FormState} from '../hooks/useFormState';
import {convertStatus} from '../utils/ComponentUtils';
import {AssetFormFields} from './AssetEditor';
import {getUniqItems} from './ListEditorUtils';

/**
 * @serialize
 *
 * This function takes the form data from the asset form and
 * serializes it into a a CTC Asset object that gets passed to the
 * addAsset service call.
 *
 * @param {object} data: The form data that needs to be transformed into
 *   an asset that can be passed to ctc to create the asset.
 */
export const serialize = (data: FormState) => {
  const {assetId, ...parsedData} = data;
  const accountData: GenericObject = {
    extensions : {},
    id         : assetId
  };
  for (const [key, obj] of Object.entries(parsedData)) {
    switch (key) {
      case AssetFormFields.Groups :
        accountData.account = obj.value;
        break;
      case AssetFormFields.Devices :
        if (!obj.value && data?.device?.value) {
          accountData.hadDevice = true;
          break;
        }
        accountData[key] = obj.value?._self ? [obj.value._self] : obj.value;
        accountData.device = obj.value;
        break;
      case AssetFormFields.Operator :
        if (obj.value) {
          accountData[key] = obj.value?._self || obj.value;
        }
        break;
      case AssetFormFields.Assets : {
        const value = data?.assets.value || obj.value;
        if (value) {
          accountData[key] = getUniqAssetLinks(value);
        }
        break;
      }
      case AssetFormFields.Device :
        if (!obj.value || !data?.devices?.value) break;
        accountData[key] = {
          name  : data.devices.value[0]?.name || data.devices.value[0]?.title,
          id    : entityId(data.devices.value[0]),
          _self : data.devices.value[0]
        };
        break;
      case AssetFormFields.IoProfile :
        if (!obj.value) break;
        accountData[key] = obj.value?._self || obj.value;
        break;
      case AssetFormFields.AssetType :
        accountData[key] = obj.value?.value || obj.value || '';
        break;
      case AssetFormFields.Schedule :
        if (obj.value) {
          accountData[key] = isString(obj.value) ? obj.value : obj.value?._self;
        }
        break;
      case AssetFormFields.EngineHoursReferenceDate :
        accountData.engineHoursReferenceTime = obj.value;
        break;
      case AssetFormFields.OverrideEngineRunTime :
      case AssetFormFields.OverrideOdometer :
      case AssetFormFields.OverrideSpeed :
        accountData.extensions[key] = Boolean(obj.value);
        break;
      case AssetFormFields.MaintenanceSchedule :
        accountData.extensions.scheduleName = isString(obj.value) ? obj.value : obj.value?.name;
        accountData[key] = obj.value;
        break;
      case AssetFormFields.FuelBurnRate :
        accountData.extensions[key] = obj.value;
        break;
      case AssetFormFields.FuelTankCapacity :
        accountData.extensions[key] = obj.value;
        break;
      case AssetFormFields.Status :
        accountData.status = convertStatus(obj.value);
        break;
      default :
        if (!obj.value && obj.value !== 0) break;
        accountData[key] = obj.value ?? null;
    }
  }
  if (!Object.keys(accountData.extensions).length) {
    delete accountData.extensions;
  }
  return accountData;
};

/**
 * @deserialize
 *
 * This function takes a CTC asset and serializes it into
 * an asset form consumable object that can be pre-populated
 * into the form. Essentially, it's the reverse of the serialize
 * function above.
 *
 * @param {Asset} asset: CTC asset that is to be deserialized into
 *   an asset form object.
 */
export const deserialize = (asset: Asset | GenericObject) => {
  if (!Object.keys(asset || {}).length) {
    return {};
  }

  const {
    account,
    engineHoursReferenceTime,
    odometerReference,
    engineHoursReference,
    extensions,
    devices,
    assets,
    operators,
    ...rest
  } = asset;

  const formData = {
    // fields not at the root object level need to be set
    // at the root level of the form
    [AssetFormFields.EngineHoursReferenceDate] : {
      name  : AssetFormFields.EngineHoursReferenceDate,
      value : engineHoursReferenceTime
    },
    [AssetFormFields.FuelBurnRate] : {
      name  : AssetFormFields.FuelBurnRate,
      value : extensions?.fuelBurnRate || null
    },
    [AssetFormFields.OverrideOdometer] : {
      name  : AssetFormFields.OverrideOdometer,
      value : extensions?.overrideOdometer || null
    },
    [AssetFormFields.OverrideEngineRunTime] : {
      name  : AssetFormFields.OverrideEngineRunTime,
      value : extensions?.overrideEngineRunTime || null
    },
    [AssetFormFields.OverrideSpeed] : {
      name  : AssetFormFields.OverrideSpeed,
      value : extensions?.overrideSpeed || null
    },
    [AssetFormFields.MaintenanceSchedule] : {
      name  : AssetFormFields.MaintenanceSchedule,
      value : isString(extensions?.scheduleName) ? extensions?.scheduleName : extensions?.scheduleName?.name
    }
  };
  // loop through the rest of the fields and set them in
  // the form
  for (const [key, obj] of Object.entries(rest)) {
    formData[key] = {
      name  : key,
      value : obj
    };
  }
  // these fields need to overwrite the existing data
  // so need to come after the loop
  formData[AssetFormFields.Groups] = {
    name  : AssetFormFields.Groups,
    value : account
  };
  formData[AssetFormFields.PreviousAccount] = {
    name  : AssetFormFields.PreviousAccount,
    value : account
  };
  formData[AssetFormFields.FuelTankCapacity] = {
    name  : AssetFormFields.FuelTankCapacity,
    value : extensions?.fuelTankCapacity || null
  };
  formData[AssetFormFields.Assets] = {
    name  : AssetFormFields.Assets,
    value : assets?.map(asset => ({
      id    : entityId(asset.href),
      name  : asset.title,
      _self : asset
    }))
  };
  formData[AssetFormFields.OdometerReference] = {
    name  : AssetFormFields.OdometerReference,
    value : odometerReference ?? null
  };
  formData[AssetFormFields.EngineHoursReference] = {
    name  : AssetFormFields.EngineHoursReference,
    value : engineHoursReference ?? null
  };
  formData[AssetFormFields.Devices] = {
    name  : AssetFormFields.Devices,
    value : devices.map(device => {
      return {
        id    : entityId(device.href),
        name  : device.title,
        _self : device,
        ...device
      };
    })
  };
  const [operator] = operators || [];
  formData[AssetFormFields.Operator] = {
    name  : AssetFormFields.Operator,
    value : operator ? {
      id    : entityId(operator.href || operator._self),
      name  : operator.title || operator.fullName,
      _self : operator._self || operator
    } : null
  };
  return formData;
};

/**
 *
 * Get the assets array and form the Link object
 * and return as unique values.
 */
export const getUniqAssetLinks = (assets: Asset[]): GroupMembers => {
  const uniqAssets = getUniqItems(assets);
  return uniqAssets.map(item => setRel(item, 'self'));
};
